import React, { useState, useEffect } from "react";
import "../styles/market.css";
import { toast } from 'react-toastify';
function Market() {
  const [transactions, setTransactions] = useState([]);
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for form inputs
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionId, setTransactionId] = useState('');
    const [username, setUsername] = useState('');
    const [amount, setAmount] = useState('');
    const [status, setStatus] = useState('');

    // State for checkbox filters
    const [filterApprove, setFilterApprove] = useState(true);
    const [filterPending, setFilterPending] = useState(true);

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    // Fetch transactions from API on component mount
    useEffect(() => {
        const fetchTransactions = async () => {
            try {
                const response = await fetch(`${baseUrl}/transactions/all`);
                console.log('Fetching from URL:', `${baseUrl}/transactions/all:`, response);
                if (!response.ok) {
                    throw new Error('Failed to fetch transactions');
                }
                const data = await response.json();
                setTransactions(data);
                setFilteredTransactions(data);
                setIsLoading(false);
            } catch (error) {
                setError(error.message);
                setIsLoading(false);
            }
        };

        fetchTransactions();
    }, []);

    // Filter transactions based on search query and checkboxes
    useEffect(() => {
        let filtered = transactions;

        // Filter by search query
        if (searchQuery) {
            filtered = filtered.filter(
                (transaction) =>
                    transaction.transaction_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    transaction.username.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Filter by status checkboxes
        if (!filterApprove || !filterPending) {
            filtered = filtered.filter((transaction) => {
                if (filterApprove && transaction.transaction_status === 'APPROVE') return true;
                if (filterPending && transaction.transaction_status === 'PENDING') return true;
                return false;
            });
        }

        setFilteredTransactions(filtered);
    }, [searchQuery, filterApprove, filterPending, transactions]);

    // Handle row click to populate text inputs
    const handleRowClick = (transaction) => {
        setSelectedTransaction(transaction);
        setTransactionId(transaction.transaction_id);
        setUsername(transaction.username);
        setAmount(transaction.transaction_amount);
        setStatus(transaction.transaction_status);
    };

    // Handle status update submission
    const handleSubmit = async () => {
        if (!selectedTransaction || !status) {
            setError('Please select a transaction and set a status');
            return;
        }

        try {
            const updateData = {
                status: status.toUpperCase(), // Ensure status is uppercase (APPROVE or PENDING)
            };

            console.log('Sending request to:', 
                `${baseUrl}/transactions/${selectedTransaction.transaction_id}`);
            console.log('Request method:', 'PATCH');
            console.log('Request body:', updateData);

            const response = await fetch(
                `${baseUrl}/transactions/${selectedTransaction.transaction_id}`,
                {
                    method: 'PATCH', // Changed to PUT
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updateData),
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', response.status, response.statusText, errorText);

                // Check if the error message is specific to the status
                const errorData = JSON.parse(errorText);
                if (errorData.message && errorData.message.includes('Only pending transactions can be updated')) {
                    // Show error toast notification using react-toastify
                    toast.error('Only pending transactions can be updated');
                } else {
                    throw new Error(`Failed to update transaction status: ${response.status} ${response.statusText} - ${errorText}`);
                }
                return; // Exit after displaying the toast
            }

            // Refetch transactions to update the table
            const updatedResponse = await fetch(`${baseUrl}/transactions/all`);
            if (!updatedResponse.ok) {
                throw new Error('Failed to fetch updated transactions');
            }
            const updatedData = await updatedResponse.json();
            setTransactions(updatedData);
            setFilteredTransactions(updatedData);

            // Clear form inputs
            setSelectedTransaction(null);
            setTransactionId('');
            setUsername('');
            setAmount('');
            setStatus('');
            setError(null);
        } catch (error) {
            setError(error.message);
            console.error('Error in handleSubmit:', error);
        }
    };

    if (isLoading) {
        return <div className="loading">Loading...</div>;
    }

    if (error) {
        return <div className="error">Error: {error}</div>;
    }

    return (
        <div className="withdraw-page">
            <div className="withdraw-content">
                {/* Form Section */}
                <h2>Withdraw Transaction </h2>
                <div className="withdraw-form">
                    <div className="form-group">
                        <input
                            type="text"
                            id="transactionId"
                            value={transactionId}
                            readOnly
                            placeholder="Select a transaction"
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            id="username"
                            value={username}
                            readOnly
                            placeholder="Select a transaction"
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="text"
                            id="amount"
                            value={amount}
                            readOnly
                            placeholder="Select a transaction"
                        />
                    </div>
                    <div className="form-group">
                        <select
                            id="status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            <option value="">Select Status</option>
                            <option value="APPROVE">APPROVE</option>
                            <option value="PENDING">PENDING</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <button
                            className="submit-button"
                            onClick={handleSubmit}
                            disabled={!selectedTransaction || !status}
                        >
                            Submit
                        </button>
                    </div>

                    <div className="action-row">
                        <div className="search-group">
                            <input
                                type="text"
                                id="search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by Transaction ID or Username..."
                            />
                        </div>
                    </div>

                    <div className="checkbox-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={filterApprove}
                                onChange={() => setFilterApprove(!filterApprove)}
                            />
                            APPROVE
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={filterPending}
                                onChange={() => setFilterPending(!filterPending)}
                            />
                            PENDING
                        </label>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-container">
                    <table className="withdraw-table">
                        <thead>
                            <tr>
                                <th>Transaction ID</th>
                                <th>Username</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTransactions.map((transaction) => (
                                <tr
                                    key={transaction.transaction_id}
                                    onClick={() => handleRowClick(transaction)}
                                    className={
                                        selectedTransaction &&
                                        selectedTransaction.transaction_id === transaction.transaction_id
                                            ? 'selected-row'
                                            : ''
                                    }
                                >
                                    <td>{transaction.transaction_id}</td>
                                    <td>{transaction.username}</td>
                                    <td>{transaction.transaction_amount}</td>
                                    <td>{transaction.transaction_status}</td>
                                    <td>{new Date(transaction.date).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Market;
